import { FC, ReactElement } from 'react'
import { Button } from 'reactstrap'

import { LoadingSpinner, TableCell, TableRow } from '../../../components'
import { EmptyState, ValkTable } from '../../../compositions'
import IconButton from '../../../compositions/@buttons/IconButton/IconButton'
import { ChangeLogEntry } from '../../../entities/ChangeLog/ChangeLog'
import { getCurrentAppVersion } from '../../../entities/ChangeLog/ChangeLogHelpers'
import { parseChangeLogEntryDateString } from '../../../entities/ChangeLog/ChangeLogTransformers'
import { formatDate } from '../../../helpers/date'
import useCheckPermission from '../../../helpers/hooks/useCheckPermission'
import trans from '../../../helpers/trans'
import { isVersionGreaterThan } from '../../../helpers/version'
import { DateFormat } from '../../../types/dateFormatTypes'

import './ChangeLogEntriesTable.scss'

const tableHeader = [
    trans('containers.tables.changeLogEntriesTable.version'),
    trans('containers.tables.changeLogEntriesTable.date'),
    trans('containers.tables.changeLogEntriesTable.intro'),
]

interface ChangeLogEntriesTableProps {
    isLoading?: boolean
    changeLogEntries: ChangeLogEntry[]
    onAddChangeLogEntryClick: () => void
    onEditChangeLogEntryClick: (changeLogEntry: ChangeLogEntry) => void
    onDeleteChangeLogEntryClick: (changeLogEntryId: string) => void
    className?: string
}

export const ChangeLogEntriesTable: FC<ChangeLogEntriesTableProps> = ({
    isLoading,
    changeLogEntries,
    onAddChangeLogEntryClick,
    onEditChangeLogEntryClick,
    onDeleteChangeLogEntryClick,
    className = '',
}): ReactElement => {
    const currentAppVersion = getCurrentAppVersion()
    const canEditChangeLogEntries = useCheckPermission('edit-all-change-log-entries')
    const canDeleteChangeLogEntries = useCheckPermission('delete-all-change-log-entries')

    if (! isLoading && ! changeLogEntries.length) {
        return (
            <EmptyState
                showButton={canEditChangeLogEntries}
                buttonText={trans('containers.tables.changeLogEntriesTable.addChangeLogEntry')}
                intro={canEditChangeLogEntries ? trans('containers.tables.changeLogEntriesTable.nullState.description') : undefined}
                title={trans('containers.tables.changeLogEntriesTable.nullState.title')}
                onAddButtonClick={onAddChangeLogEntryClick}
                className="change-log-entries-table__empty-state"
            />
        )
    }

    return (
        <div className={`change-log-entries-table ${className}`}>
            {isLoading && <LoadingSpinner />}

            {!! changeLogEntries.length && ! isLoading && (
                <>
                    {canEditChangeLogEntries && (
                        <Button
                            type="button"
                            onClick={onAddChangeLogEntryClick}
                            color="orange"
                            className="change-log-entries-table__add-button"
                        >
                            {trans('containers.tables.changeLogEntriesTable.addChangeLogEntry')}
                        </Button>
                    )}

                    <ValkTable<ChangeLogEntry>
                        rows={changeLogEntries}
                        tableHeaders={tableHeader}
                        tableClassName="change-log-entries-table__table"
                    >
                        {(changeLogEntryRows): ReactElement[] => changeLogEntryRows.map(changeLogEntry => (
                            <TableRow hasHoverState key={changeLogEntry.id} className="change-log-entries-table__row">
                                <TableCell>
                                    {changeLogEntry.version}
                                    {isVersionGreaterThan(changeLogEntry.version, currentAppVersion) && (
                                        <span className="change-log-entries-table__not-yet-released">
                                            {trans('containers.tables.changeLogEntriesTable.notYetReleased')}
                                        </span>
                                    )}
                                </TableCell>

                                <TableCell>
                                    {formatDate(parseChangeLogEntryDateString(changeLogEntry.date), DateFormat.dateWithHoursMinutes)}
                                </TableCell>

                                <TableCell className="change-log-entries-table__cell-auto">
                                    {changeLogEntry.intro}
                                </TableCell>

                                <TableCell className="change-log-entries-table__cell" contentClassName="d-flex">
                                    {canEditChangeLogEntries && (
                                        <IconButton
                                            hideLabel
                                            text="edit"
                                            icon="edit"
                                            onClick={() => onEditChangeLogEntryClick(changeLogEntry)}
                                            iconClassName="change-log-entries-table__edit-icon"
                                        />
                                    )}

                                    {canDeleteChangeLogEntries && (
                                        <IconButton
                                            hideLabel
                                            text="delete"
                                            icon="trash"
                                            onClick={() => onDeleteChangeLogEntryClick(changeLogEntry.id)}
                                            className="change-log-entries-table__delete"
                                            iconClassName="change-log-entries-table__delete-icon"
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </ValkTable>
                </>
            )}
        </div>
    )
}
